/**
 * Section B7.5 Service - B.7.5.事前資料參數說明
 * Data and Parameters Fixed Ex Ante
 */
import axios from '../api/axios';

const BASE_URL = '/api/section-b7-5';

// ── B.7.4 來源參照 ─────────────────────────────────────────────────

export interface B74RefEntry {
  symbol: string;
  para_type: number | null;   // 249=事前固定, null=皆適用
  equation_name: string;
  equation: string;
}

// ── parameters JSON 子結構 ─────────────────────────────────────────

export interface ParameterEntry {
  id: string;                              // UUID v4
  data_parameter: string;                  // LaTeX 語法，參數符號
  description: string;                     // 純文字，參數說明
  data_unit: string;                       // LaTeX 語法，單位
  equations_referred: string;              // 純文字，引用的方程式
  purpose_baseline: boolean;               // 用途：基準線排放/移除量
  purpose_project: boolean;                // 用途：專案排放/移除量
  purpose_leakage: boolean;                // 用途：洩漏排放
  values_applied: string | null;           // 富文本 (HTML)
  source: 'measured' | 'other' | null;     // 資料來源
  measurement_methods: string | null;      // 富文本 (HTML)
  additional_comments: string | null;      // 富文本 (HTML，選填)
  b74_ref: B74RefEntry | null;             // B.7.4 來源參照；手動新增時為 null
}

// ── 主資料表 ──────────────────────────────────────────────────────

export interface SectionB75Detail {
  id: number;
  organization_id: number;
  project_id: number;
  parameters: ParameterEntry[] | null;
  b7_5: number;
  edit_by: number;
  created_at: string;
  updated_at: string | null;
  built: number;    // 已建立的 B.7.4 必要參數數量
  total: number;    // B.7.4 必要參數總數
}

// ── B.7.4 可用參數（下拉選單）────────────────────────────────────

export interface B74AvailableParam {
  symbol: string;
  para_type: number | null;
  unit: string;
  equation_name: string;
  equation: string;
}

export interface B74AvailableParamsResponse {
  available: B74AvailableParam[];
  built: number;
  total: number;
}

// ── Request Payloads ──────────────────────────────────────────────

export interface ParameterEntryCreate {
  data_parameter: string;
  description: string;
  data_unit: string;
  equations_referred: string;
  purpose_baseline: boolean;
  purpose_project: boolean;
  purpose_leakage: boolean;
  values_applied?: string | null;
  source?: 'measured' | 'other' | null;
  measurement_methods?: string | null;
  additional_comments?: string | null;
  b74_ref?: B74RefEntry | null;
}

export interface ParameterEntryUpdate {
  data_parameter?: string;
  description?: string;
  data_unit?: string;
  equations_referred?: string;
  purpose_baseline?: boolean;
  purpose_project?: boolean;
  purpose_leakage?: boolean;
  values_applied?: string | null;
  source?: 'measured' | 'other' | null;
  measurement_methods?: string | null;
  additional_comments?: string | null;
}

// ── API 函式 ──────────────────────────────────────────────────────

export const getSectionB75ByProject = async (projectId: number): Promise<SectionB75Detail | null> => {
  const response = await axios.get<SectionB75Detail>(`${BASE_URL}/by-project/${projectId}`);
  return response.data;
};

export const getB74AvailableParams = async (projectId: number): Promise<B74AvailableParamsResponse> => {
  const response = await axios.get<B74AvailableParamsResponse>(`${BASE_URL}/${projectId}/b74-available-params`);
  return response.data;
};

export const addParameter = async (projectId: number, entry: ParameterEntryCreate): Promise<SectionB75Detail> => {
  const response = await axios.post<SectionB75Detail>(`${BASE_URL}/${projectId}/parameters`, entry);
  return response.data;
};

export const updateParameter = async (
  projectId: number,
  entryId: string,
  entry: ParameterEntryUpdate
): Promise<SectionB75Detail> => {
  const response = await axios.put<SectionB75Detail>(`${BASE_URL}/${projectId}/parameters/${entryId}`, entry);
  return response.data;
};

export const deleteParameter = async (projectId: number, entryId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${projectId}/parameters/${entryId}`);
};

export const resetSectionB75 = async (projectId: number): Promise<void> => {
  await axios.post(`${BASE_URL}/${projectId}/reset`);
};

const sectionB75Service = {
  getSectionB75ByProject,
  getB74AvailableParams,
  addParameter,
  updateParameter,
  deleteParameter,
  resetSectionB75,
};
export default sectionB75Service;
